import apiClient from './apiClient';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface User {
  _id: string;
  name: string;
  username: string;
  email?: string;
  phone?: string;
  countryCode?: string;
  profileImage?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  isPrivate?: boolean;
  isVerified?: boolean;
  isAdmin?: boolean;
  isActive?: boolean;
  isBanned?: boolean;
  isSuspended?: boolean;
  banReason?: string;
  suspendedUntil?: string;
  postingRestricted?: boolean;
  marketplaceRestricted?: boolean;
  postsCount?: number;
  followersCount?: number;
  followingCount?: number;
  petsCount?: number;
  bio?: string;
  createdOn: string;
  lastUpdate: string;
}

export interface UserFilters {
  search?: string;
  isActive?: boolean;
  isVerified?: boolean;
  isAdmin?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

class UsersService {
  async getUsers(filters: UserFilters = {}) {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.isVerified !== undefined) params.append('isVerified', filters.isVerified.toString());
    if (filters.isAdmin !== undefined) params.append('isAdmin', filters.isAdmin.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = queryString ? `/users?${queryString}` : '/users';
    
    console.log('📡 Fetching users from:', url);
    const response = await apiClient.get<ApiResponse<{ users: User[]; pagination: any }>>(url);
    console.log('📡 Users API response:', response.data);
    
    if (!response.data.success) {
      console.error('❌ Users API error:', response.data.message);
      throw new Error(response.data.message || 'Failed to fetch users');
    }
    
    if (!response.data.data) {
      console.warn('⚠️ No data in response');
      return { users: [], pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 20 } };
    }
    
    console.log('✅ Users data:', response.data.data);
    return response.data.data;
  }

  async getUserById(id: string) {
    const response = await apiClient.get<ApiResponse<User>>(`/users/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch user');
    }
    return response.data.data;
  }

  async updateUser(id: string, data: Partial<User>) {
    const response = await apiClient.put<ApiResponse<User>>(`/users/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update user');
    }
    return response.data.data;
  }

  async deleteUser(id: string) {
    const response = await apiClient.delete<ApiResponse<void>>(`/users/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete user');
    }
    return response.data;
  }

  async toggleVerification(id: string) {
    const response = await apiClient.patch<ApiResponse<User>>(`/users/${id}/verify`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to toggle verification');
    }
    return response.data.data;
  }

  async suspendUser(id: string, reason?: string, suspendedUntil?: string) {
    const response = await apiClient.post<ApiResponse<User>>(`/users/${id}/suspend`, {
      reason,
      suspendedUntil,
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to suspend user');
    }
    return response.data.data;
  }

  async banUser(id: string, reason?: string) {
    const response = await apiClient.post<ApiResponse<User>>(`/users/${id}/ban`, { reason });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to ban user');
    }
    return response.data.data;
  }

  async unblockUser(id: string) {
    const response = await apiClient.post<ApiResponse<User>>(`/users/${id}/unblock`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unblock user');
    }
    return response.data.data;
  }

  async togglePostingRestriction(id: string) {
    const response = await apiClient.patch<ApiResponse<User>>(`/users/${id}/restrict-posting`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to toggle posting restriction');
    }
    return response.data.data;
  }

  async toggleMarketplaceRestriction(id: string) {
    const response = await apiClient.patch<ApiResponse<User>>(`/users/${id}/restrict-marketplace`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to toggle marketplace restriction');
    }
    return response.data.data;
  }

  async bulkUserPostsAction(id: string, action: 'delete' | 'disable') {
    const response = await apiClient.post<ApiResponse<{ deletedCount?: number; disabledCount?: number; activePostsCount?: number; action: string }>>(
      `/users/${id}/posts/bulk-action`,
      { action }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to perform bulk action on user posts');
    }
    return response.data.data;
  }
}

export const usersService = new UsersService();
export default usersService;

